import type { ComponentProps } from 'react'

import { useTranslation } from 'react-i18next'

import { Icon } from '@/components/icon'
import { Button } from '@/components/ui'
import { cn } from '@/infra'

import type {
  TDocumentoAnexado,
  TDocumentoEnviando,
  TDocumentoError,
  TDocumentoRecuperado,
  TDocumentoSucesso,
  TTipoDocumento,
} from './types'

const useRotuloTipoDocumento = () => {
  const { t } = useTranslation('common')
  return (tipo: TTipoDocumento) => t(`documentos.tipos.${tipo}`)
}

const CardDocumentoRoot = ({ children, className, ...props }: ComponentProps<'div'>) => {
  return (
    <div className={cn('bg-muted/50 flex gap-4 rounded-md px-4 py-2', className)} {...props}>
      {children}
    </div>
  )
}

const CardDocumentoIcon = (props: ComponentProps<typeof Icon>) => {
  return (
    <div
      className="border-input flex h-9 w-9 items-center justify-center rounded-md border bg-white"
      aria-hidden="true"
    >
      <Icon {...props} />
    </div>
  )
}

const CardDocumentoContent = ({ children }: ComponentProps<'div'>) => {
  return <div className="font-nunito flex-1 text-sm font-medium">{children}</div>
}

const CardDocumentoTipo = ({ children, ...props }: ComponentProps<'h4'>) => {
  const { t } = useTranslation('common')

  return (
    <h4 className="text-neutral-800" {...props}>
      {children || t('documentos.card.tipo_padrao')}
    </h4>
  )
}

const CardDocumentoNome = (props: ComponentProps<'p'>) => {
  return <p className="text-neutral-600" {...props} />
}
const CardDocumentoTamanho = (props: ComponentProps<'p'>) => {
  return <p className="font-normal text-gray-500" {...props} />
}

const CardDocumentoErrorMessage = (props: ComponentProps<'p'>) => {
  return <p className="text-destructive font-normal" {...props} />
}

const CardDocumentoErrorActions = ({ onRetry, onDelete }: { onRetry: VoidFunction; onDelete: VoidFunction }) => {
  const { t } = useTranslation('common')

  return (
    <div>
      <Button variant="ghost" className="text-primary hover:bg-primary/5 hover:text-primary/90 h-6" onClick={onRetry}>
        <Icon name="refresh-cw" className="mr-1 h-4 w-4" />
        {t('documentos.card.reenviar')}
      </Button>
      <Button
        variant="ghost"
        className="text-destructive hover:bg-destructive/5 hover:text-destructive/90 h-6"
        onClick={onDelete}
      >
        <Icon name="trash-2" className="mr-1 h-4 w-4" />
        {t('documentos.card.excluir')}
      </Button>
    </div>
  )
}

const CardDocumentoIconSkeleton = () => {
  return (
    <div className="border-input flex h-9 w-9 items-center justify-center rounded-md border bg-white">
      <div className="h-5 w-5 animate-pulse rounded-full bg-gray-300" />
    </div>
  )
}

const CardDocumentoSkeleton = () => {
  return (
    <CardDocumentoRoot>
      <CardDocumentoIconSkeleton />
      <CardDocumentoContent>
        <div className="space-y-2">
          <div className="h-4 w-24 animate-pulse rounded bg-gray-300" />
          <div className="h-4 w-40 animate-pulse rounded bg-gray-200" />
          <div className="h-3 w-16 animate-pulse rounded bg-gray-200" />
        </div>
      </CardDocumentoContent>
    </CardDocumentoRoot>
  )
}

const CardDocumentoSuccess = ({ documento }: { documento: TDocumentoSucesso }) => {
  const rotuloDoTipo = useRotuloTipoDocumento()

  return (
    <CardDocumentoRoot className={cn(!documento.nome && 'items-center')}>
      <CardDocumentoIcon name="circle-check-big" className="h-5 w-5 text-green-800" />
      <CardDocumentoContent>
        <CardDocumentoTipo>{rotuloDoTipo(documento.tipo)}</CardDocumentoTipo>
        {documento.nome && <CardDocumentoNome>{documento.nome}</CardDocumentoNome>}
        {documento.tamanho && <CardDocumentoTamanho>{documento.tamanho}</CardDocumentoTamanho>}
      </CardDocumentoContent>
    </CardDocumentoRoot>
  )
}

const CardDocumentoError = ({
  documento,
  onRetry,
  onDelete,
}: {
  documento: TDocumentoError
  onRetry: VoidFunction
  onDelete: VoidFunction
}) => {
  const { t } = useTranslation('common')
  const rotuloDoTipo = useRotuloTipoDocumento()

  return (
    <CardDocumentoRoot className="border-destructive border">
      <CardDocumentoIcon name="triangle-alert" className="h-5 w-5" />
      <CardDocumentoContent>
        <CardDocumentoTipo>{rotuloDoTipo(documento.tipo)}</CardDocumentoTipo>
        {documento.nome && <CardDocumentoNome>{documento.nome}</CardDocumentoNome>}
        <CardDocumentoErrorMessage>{t('documentos.card.erro_envio')}</CardDocumentoErrorMessage>
      </CardDocumentoContent>
      <CardDocumentoErrorActions onRetry={onRetry} onDelete={onDelete} />
    </CardDocumentoRoot>
  )
}
const CardDocumentoRecovered = ({ documento }: { documento: TDocumentoRecuperado }) => {
  const rotuloDoTipo = useRotuloTipoDocumento()

  return (
    <CardDocumentoRoot className="items-center">
      <CardDocumentoIcon name="circle-check-big" className="h-5 w-5 text-green-800" />
      <CardDocumentoContent>
        <CardDocumentoTipo>{rotuloDoTipo(documento.tipo)}</CardDocumentoTipo>
      </CardDocumentoContent>
    </CardDocumentoRoot>
  )
}

const CardDocumentoUploading = ({ documento }: { documento: TDocumentoEnviando }) => {
  const rotuloDoTipo = useRotuloTipoDocumento()

  return (
    <CardDocumentoRoot className={cn(!documento.nome && 'items-center')}>
      <CardDocumentoIcon name="loader" className="h-5 w-5 animate-spin" />
      <CardDocumentoContent>
        <CardDocumentoTipo>{rotuloDoTipo(documento.tipo)}</CardDocumentoTipo>
        {documento.nome && <CardDocumentoNome>{documento.nome}</CardDocumentoNome>}
        {documento.tamanho && <CardDocumentoTamanho>{documento.tamanho}</CardDocumentoTamanho>}
      </CardDocumentoContent>
    </CardDocumentoRoot>
  )
}

interface CardDocumentoPropostaProps {
  documento: TDocumentoAnexado
  onRetry: (documento: TDocumentoAnexado) => void
  onDelete: (documento: TDocumentoAnexado) => void
}

const CardDocumento = ({ documento, onRetry, onDelete }: CardDocumentoPropostaProps) => {
  switch (documento.status) {
    case 'recovered':
      return <CardDocumentoRecovered documento={documento} />
    case 'success':
      return <CardDocumentoSuccess documento={documento} />
    case 'error':
      return (
        <CardDocumentoError
          documento={documento}
          onRetry={() => onRetry(documento)}
          onDelete={() => onDelete(documento)}
        />
      )
    default:
      return <CardDocumentoUploading documento={documento} />
  }
}

export {
  CardDocumentoRoot,
  CardDocumentoIcon,
  CardDocumentoNome,
  CardDocumentoTipo,
  CardDocumentoErrorMessage,
  CardDocumentoContent,
  CardDocumentoTamanho,
  CardDocumentoErrorActions,
  CardDocumentoSkeleton,
  CardDocumento,
}
